/**
 * Legacy Config service — mirrors Angular calls against /api/config.
 * Only the endpoints the Communication tab needs are wired for now.
 * Remaining tabs will extend this service as they migrate.
 */

import apiClient from '@/lib/api/apiClient'
import { trackApplicationEvent } from '@/lib/utils/analytics'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import type {
  ApplicantFormValues,
  CommunicationFormValues,
  ConfigApiResponse,
  CreditReportKind,
  ImapTestPayload,
  MaintenanceFlag,
  MobileAppFormValues,
  NinjaVersion,
  OnOff,
  PaymentFormValues,
  PaymentGatewayFormValues,
  PlaidEnv,
  SecurityFormValues,
  YardiFormValues,
  YardiPlaceholders,
  YesNo,
  ZeroOne,
} from '../types/config'
import { normalizeCaptureTimeForInput } from '../utils/paymentFieldValidation'

/** GET /api/config — full raw config map. */
export async function getConfig(): Promise<ConfigApiResponse> {
  try {
    const response = await apiClient.get<ConfigApiResponse>('/api/config')
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load config')
  }
}

/** POST /api/config — save a partial config payload (keyed by config name). */
export async function saveConfig(payload: Record<string, unknown>): Promise<void> {
  try {
    await apiClient.post('/api/config', payload)
    trackApplicationEvent('config_added', 'configuration')
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to save config')
  }
}

/** POST /api/config/test/imap — server-side IMAP handshake test. */
export async function testImap(payload: ImapTestPayload): Promise<void> {
  try {
    await apiClient.post('/api/config/test/imap', payload)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'IMAP validation failed')
  }
}

/**
 * POST /api/tagged-backup — take a database snapshot. Cornerstone-only.
 * Returns the server's response body verbatim (string or object describing
 * the snapshot). Mirrors Angular `takeBackup(description)`.
 */
export async function takeBackup(description: string): Promise<string> {
  try {
    const body: Record<string, string> = {}
    if (description) body.description = description
    const response = await apiClient.post('/api/tagged-backup', body)
    const data: unknown = response.data
    return typeof data === 'string' ? data : JSON.stringify(data)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Backup failed')
  }
}

/**
 * Flatten the scalar `{ name, value }` entries of /api/config into
 * a plain `{ [name]: stringValue }` map. Nested gateway objects
 * (PAYPAL, CARDCONNECT, UPTEMPO, PAYNEARME) are ignored here — use
 * `extractPaymentGateway` for those.
 */
export function flattenScalarEntries(raw: ConfigApiResponse): Record<string, string> {
  const flat: Record<string, string> = {}
  Object.values(raw).forEach((entry) => {
    if (
      entry &&
      typeof entry === 'object' &&
      'name' in entry &&
      'value' in entry
    ) {
      const { name, value } = entry as { name: string; value: unknown }
      if (typeof name === 'string') {
        flat[name] = value == null ? '' : String(value)
      }
    }
  })
  return flat
}

/**
 * Pull a nested payment-gateway config object (PAYPAL/CARDCONNECT/etc.)
 * from the raw /api/config response and normalise YES/NO → boolean for
 * use_for_app, allow_invoice, allow_refund. Returns undefined if the
 * gateway key isn't in the response.
 */
export function extractPaymentGateway(
  raw: ConfigApiResponse,
  key: 'PAYPAL' | 'CARDCONNECT' | 'UPTEMPO' | 'PAYNEARME',
): {
  payment_status: 'ON' | 'OFF' | ''
  certificate_surcharge: string
  service_fee: string
  display_order: string
  use_for_app: boolean
  allow_invoice: boolean
  allow_refund: boolean
  refund_fee: string
} {
  const raw_ = raw[key]
  const obj =
    raw_ && typeof raw_ === 'object' && !('name' in raw_) && !('value' in raw_)
      ? (raw_ as Record<string, unknown>)
      : {}
  const asString = (v: unknown) => (v == null ? '' : String(v))
  const toStatus = (v: unknown): 'ON' | 'OFF' | '' =>
    v === 'ON' || v === 'OFF' ? v : ''
  const toBool = (v: unknown) => v === 'YES' || v === true
  return {
    payment_status: toStatus(obj.payment_status),
    certificate_surcharge: asString(obj.certificate_surcharge),
    service_fee: asString(obj.service_fee),
    display_order: asString(obj.display_order),
    use_for_app: toBool(obj.use_for_app),
    allow_invoice: toBool(obj.allow_invoice),
    allow_refund: toBool(obj.allow_refund),
    refund_fee: asString(obj.refund_fee),
  }
}

/**
 * Build a `{ [KEY]: trimmedValue }` payload from a values object, dropping
 * empty strings so Angular's `getFilterValue` semantics are preserved.
 * Use this for any tab that only sends scalar config keys.
 */
export function buildScalarPayload<T>(
  values: T,
  keys: readonly (keyof T)[],
): Record<string, string> {
  const payload: Record<string, string> = {}
  keys.forEach((key) => {
    const raw = (values as Record<string, unknown>)[String(key)]
    const trimmed = typeof raw === 'string' ? raw.trim() : ''
    if (trimmed !== '') {
      payload[String(key)] = trimmed
    }
  })
  return payload
}

/* --------------------------------------------------------------------- */
/* Applicant tab                                                          */
/* --------------------------------------------------------------------- */

const APPLICANT_KEYS = [
  'REFUND_DELAY',
  'DEFAULT_LEASE_LEAD_TIME',
  'MAX_LEASE_LEAD_TIME',
  'CREDIT_REPORT',
  'CORELOGIC_AUTO_APPROVE',
  'CORELOGIC_SCORE_APPROVE',
  'CORELOGIC_SCORE_DENY',
  'EQUIFAX_CUSTOMER_NUMBER',
  'EQUIFAX_SECURITY_CODE',
  'EQUIFAX_SITE_ID',
  'EQUIFAX_SITE_PASSWORD',
  'EQUIFAX_SERVICE_NAME',
  'EQUIFAX_XML_API',
  'EQUIFAX_CUSTOMER_NUMBER_JSON',
  'EQUIFAX_SECURITY_CODE_JSON',
  'EQUIFAX_JSON_CLIENT_ID',
  'EQUIFAX_JSON_SECRET',
  'EQUIFAX_JSON_API',
  'PLAID_ENV',
  'PLAID_CLIENT_ID_sandbox',
  'PLAID_SECRET_sandbox',
  'PLAID_CLIENT_ID_development',
  'PLAID_SECRET_development',
  'PLAID_CLIENT_ID_production',
  'PLAID_SECRET_production',
  'NINJA_REQUIRED',
  'NINJA_VERSION',
  'NINJA_JS_URL',
  'NINJA_API_URL',
  'NINJA_API_KEY',
  'NINJA_JS_URL_V2',
  'NINJA_API_URL_V2',
  'NINJA_API_KEY_V2',
  'NINJA_AUTH_API_URL',
  'VERIFAST_ENABLED',
  'VERIFAST_ID_ONLY',
  'VERIFAST_INVITE',
  'VERIFAST_API_URL',
  'VERIFAST_APPLICANT_HOST',
  'VERIFAST_WEBHOOK_URL',
  'VERIFAST_APPLICATION_ID',
  'VERIFAST_API_KEY',
  'VERIFAST_API_SECRET',
  'VERIFAST_WEBHOOK_SECRET',
] as const

export function extractApplicantValues(raw: ConfigApiResponse): ApplicantFormValues {
  const flat = flattenScalarEntries(raw)
  const crKind: CreditReportKind =
    flat.CREDIT_REPORT === 'ZipReport' ||
    flat.CREDIT_REPORT === 'CoreLogic' ||
    flat.CREDIT_REPORT === 'Equifax'
      ? flat.CREDIT_REPORT
      : ''
  const autoApprove: OnOff =
    flat.CORELOGIC_AUTO_APPROVE === 'ON' || flat.CORELOGIC_AUTO_APPROVE === 'OFF'
      ? flat.CORELOGIC_AUTO_APPROVE
      : ''
  const plaidEnv: PlaidEnv =
    flat.PLAID_ENV === 'sandbox' ||
    flat.PLAID_ENV === 'development' ||
    flat.PLAID_ENV === 'production'
      ? flat.PLAID_ENV
      : ''
  const ninjaRequired: YesNo =
    flat.NINJA_REQUIRED === 'YES' || flat.NINJA_REQUIRED === 'NO' ? flat.NINJA_REQUIRED : ''
  const ninjaVersion: NinjaVersion =
    flat.NINJA_VERSION === 'V1' || flat.NINJA_VERSION === 'V2' ? flat.NINJA_VERSION : ''
  const zeroOne = (value?: string): ZeroOne =>
    value === '0' || value === '1' ? value : ''

  return {
    REFUND_DELAY: flat.REFUND_DELAY ?? '',
    DEFAULT_LEASE_LEAD_TIME: flat.DEFAULT_LEASE_LEAD_TIME ?? '',
    MAX_LEASE_LEAD_TIME: flat.MAX_LEASE_LEAD_TIME ?? '',
    CREDIT_REPORT: crKind,
    CORELOGIC_AUTO_APPROVE: autoApprove,
    CORELOGIC_SCORE_APPROVE: flat.CORELOGIC_SCORE_APPROVE ?? '',
    CORELOGIC_SCORE_DENY: flat.CORELOGIC_SCORE_DENY ?? '',
    EQUIFAX_CUSTOMER_NUMBER: flat.EQUIFAX_CUSTOMER_NUMBER ?? '',
    EQUIFAX_SECURITY_CODE: flat.EQUIFAX_SECURITY_CODE ?? '',
    EQUIFAX_SITE_ID: flat.EQUIFAX_SITE_ID ?? '',
    EQUIFAX_SITE_PASSWORD: flat.EQUIFAX_SITE_PASSWORD ?? '',
    EQUIFAX_SERVICE_NAME: flat.EQUIFAX_SERVICE_NAME ?? '',
    EQUIFAX_XML_API: flat.EQUIFAX_XML_API ?? '',
    EQUIFAX_CUSTOMER_NUMBER_JSON: flat.EQUIFAX_CUSTOMER_NUMBER_JSON ?? '',
    EQUIFAX_SECURITY_CODE_JSON: flat.EQUIFAX_SECURITY_CODE_JSON ?? '',
    EQUIFAX_JSON_CLIENT_ID: flat.EQUIFAX_JSON_CLIENT_ID ?? '',
    EQUIFAX_JSON_SECRET: flat.EQUIFAX_JSON_SECRET ?? '',
    EQUIFAX_JSON_API: flat.EQUIFAX_JSON_API ?? '',
    PLAID_ENV: plaidEnv,
    PLAID_CLIENT_ID_sandbox: flat.PLAID_CLIENT_ID_sandbox ?? '',
    PLAID_SECRET_sandbox: flat.PLAID_SECRET_sandbox ?? '',
    PLAID_CLIENT_ID_development: flat.PLAID_CLIENT_ID_development ?? '',
    PLAID_SECRET_development: flat.PLAID_SECRET_development ?? '',
    PLAID_CLIENT_ID_production: flat.PLAID_CLIENT_ID_production ?? '',
    PLAID_SECRET_production: flat.PLAID_SECRET_production ?? '',
    NINJA_REQUIRED: ninjaRequired,
    NINJA_VERSION: ninjaVersion,
    NINJA_JS_URL: flat.NINJA_JS_URL ?? '',
    NINJA_API_URL: flat.NINJA_API_URL ?? '',
    NINJA_API_KEY: flat.NINJA_API_KEY ?? '',
    NINJA_JS_URL_V2: flat.NINJA_JS_URL_V2 ?? '',
    NINJA_API_URL_V2: flat.NINJA_API_URL_V2 ?? '',
    NINJA_API_KEY_V2: flat.NINJA_API_KEY_V2 ?? '',
    NINJA_AUTH_API_URL: flat.NINJA_AUTH_API_URL ?? '',
    VERIFAST_ENABLED: zeroOne(flat.VERIFAST_ENABLED),
    VERIFAST_ID_ONLY: zeroOne(flat.VERIFAST_ID_ONLY),
    VERIFAST_INVITE: zeroOne(flat.VERIFAST_INVITE),
    VERIFAST_API_URL: flat.VERIFAST_API_URL ?? '',
    VERIFAST_APPLICANT_HOST: flat.VERIFAST_APPLICANT_HOST ?? '',
    VERIFAST_WEBHOOK_URL: flat.VERIFAST_WEBHOOK_URL ?? '',
    VERIFAST_APPLICATION_ID: flat.VERIFAST_APPLICATION_ID ?? '',
    VERIFAST_API_KEY: flat.VERIFAST_API_KEY ?? '',
    VERIFAST_API_SECRET: flat.VERIFAST_API_SECRET ?? '',
    VERIFAST_WEBHOOK_SECRET: flat.VERIFAST_WEBHOOK_SECRET ?? '',
  }
}

export function buildApplicantPayload(
  values: ApplicantFormValues,
): Record<string, string> {
  return buildScalarPayload(values, APPLICANT_KEYS)
}

/* --------------------------------------------------------------------- */
/* MRG Percent grid (read-only, lives inside the Payments tab)            */
/* --------------------------------------------------------------------- */

import type { MrgRow } from '../types'

export type { MrgRow } from '../types'

/**
 * Mirrors Angular `getFlattenedMrgData()`: walks the last 15 years and
 * flattens `configData.MRG_PERCENT[year] = [{percent, effectiveStartDate}]`
 * into a flat list. Only rows that have an `effectiveStartDate` are
 * rendered (Angular template guards each row with `*ngIf="row.effectiveStartDate"`).
 */
export function extractMrgRows(raw: ConfigApiResponse): MrgRow[] {
  const block = (raw as Record<string, unknown>).MRG_PERCENT
  if (!block || typeof block !== 'object') return []
  const map = block as Record<string, unknown>
  const currentYear = new Date().getFullYear()
  const rows: MrgRow[] = []
  for (let i = 0; i < 15; i++) {
    const year = currentYear - i
    const yearEntries = map[String(year)]
    if (!Array.isArray(yearEntries)) continue
    yearEntries.forEach((entry) => {
      if (!entry || typeof entry !== 'object') return
      const e = entry as Record<string, unknown>
      const date = typeof e.effectiveStartDate === 'string' ? e.effectiveStartDate : ''
      if (!date) return
      rows.push({
        year,
        percent: e.percent == null ? '' : String(e.percent),
        effectiveStartDate: date,
      })
    })
  }
  return rows
}

/** Format an ISO/UTC date string to MM/DD/YYYY (matches Angular `mrgFormatDate`). */
export function formatMrgDate(value: string): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${mm}/${dd}/${date.getFullYear()}`
}

/* --------------------------------------------------------------------- */
/* Payments tab                                                           */
/* --------------------------------------------------------------------- */

const PAYMENT_SCALAR_KEYS = [
  'CARDCONNECT_USERNAME',
  'CARDCONNECT_PASSWORD',
  'CARDCONNECT_MERCHANT_ID',
  'CARDCONNECT_ENDPOINT',
  'CARDCONNECT_CAPTURE_TIME',
  'DEFAULT_ACH_STATUS',
  'UPTEMPO_PROGRAM_STATUS',
  'PAYMENT_WINDOW',
] as const

/**
 * Angular `submitForm()` only maps truthy payment options to `'YES'`/`'NO'`;
 * unchecked options stay boolean `false`. Empty `refund_fee` is sent as `null`.
 * Sending `'NO'` for false flags causes HTTP 500 on POST `/api/config`.
 */
function paymentOptionToApi(checked: boolean): 'YES' | false {
  return checked ? 'YES' : false
}

function gatewayToApi(gw: PaymentGatewayFormValues): Record<string, unknown> {
  const payload: Record<string, unknown> = {}
  if (gw.payment_status) payload.payment_status = gw.payment_status
  if (gw.certificate_surcharge.trim() !== '')
    payload.certificate_surcharge = gw.certificate_surcharge.trim()
  if (gw.service_fee.trim() !== '') payload.service_fee = gw.service_fee.trim()
  if (gw.display_order.trim() !== '') payload.display_order = gw.display_order.trim()
  payload.use_for_app = paymentOptionToApi(gw.use_for_app)
  payload.allow_invoice = paymentOptionToApi(gw.allow_invoice)
  payload.allow_refund = paymentOptionToApi(gw.allow_refund)
  const refundFee = gw.refund_fee.trim()
  payload.refund_fee = refundFee !== '' ? refundFee : null
  return payload
}

export function extractPaymentValues(raw: ConfigApiResponse): PaymentFormValues {
  const flat = flattenScalarEntries(raw)
  const ach: OnOff =
    flat.DEFAULT_ACH_STATUS === 'ON' || flat.DEFAULT_ACH_STATUS === 'OFF'
      ? flat.DEFAULT_ACH_STATUS
      : ''
  const up: OnOff =
    flat.UPTEMPO_PROGRAM_STATUS === 'ON' || flat.UPTEMPO_PROGRAM_STATUS === 'OFF'
      ? flat.UPTEMPO_PROGRAM_STATUS
      : ''

  return {
    PAYPAL: extractPaymentGateway(raw, 'PAYPAL'),
    CARDCONNECT: extractPaymentGateway(raw, 'CARDCONNECT'),
    PAYNEARME: extractPaymentGateway(raw, 'PAYNEARME'),
    UPTEMPO: extractPaymentGateway(raw, 'UPTEMPO'),
    CARDCONNECT_USERNAME: flat.CARDCONNECT_USERNAME ?? '',
    CARDCONNECT_PASSWORD: flat.CARDCONNECT_PASSWORD ?? '',
    CARDCONNECT_MERCHANT_ID: flat.CARDCONNECT_MERCHANT_ID ?? '',
    CARDCONNECT_ENDPOINT: flat.CARDCONNECT_ENDPOINT ?? '',
    CARDCONNECT_CAPTURE_TIME: normalizeCaptureTimeForInput(flat.CARDCONNECT_CAPTURE_TIME),
    DEFAULT_ACH_STATUS: ach,
    UPTEMPO_PROGRAM_STATUS: up,
    PAYMENT_WINDOW: flat.PAYMENT_WINDOW ?? '',
  }
}

export function buildPaymentPayload(values: PaymentFormValues): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    PAYPAL: gatewayToApi(values.PAYPAL),
    CARDCONNECT: gatewayToApi(values.CARDCONNECT),
    PAYNEARME: gatewayToApi(values.PAYNEARME),
    UPTEMPO: gatewayToApi(values.UPTEMPO),
  }
  const scalar = buildScalarPayload(values, PAYMENT_SCALAR_KEYS)
  return { ...payload, ...scalar }
}

/* --------------------------------------------------------------------- */
/* Security tab                                                           */
/* --------------------------------------------------------------------- */

const SECURITY_KEYS = [
  'GOOGLE_CAPTCHA',
  'SESSION_TIMEOUT',
  'OTP_SMS_CONTENT',
  'OTP_BACKUP',
  'NOTIFICATION_SHOW',
  'NOTIFICATION_CONTENT',
  'NOTIFICATION_TIME',
] as const

export function extractSecurityValues(raw: ConfigApiResponse): SecurityFormValues {
  const flat = flattenScalarEntries(raw)
  const captcha: OnOff =
    flat.GOOGLE_CAPTCHA === 'ON' || flat.GOOGLE_CAPTCHA === 'OFF' ? flat.GOOGLE_CAPTCHA : ''
  const notifShow: OnOff =
    flat.NOTIFICATION_SHOW === 'ON' || flat.NOTIFICATION_SHOW === 'OFF'
      ? flat.NOTIFICATION_SHOW
      : ''
  return {
    GOOGLE_CAPTCHA: captcha,
    SESSION_TIMEOUT: flat.SESSION_TIMEOUT ?? '',
    OTP_SMS_CONTENT: flat.OTP_SMS_CONTENT ?? '',
    OTP_BACKUP: flat.OTP_BACKUP ?? '',
    NOTIFICATION_SHOW: notifShow,
    NOTIFICATION_CONTENT: flat.NOTIFICATION_CONTENT ?? '',
    NOTIFICATION_TIME: flat.NOTIFICATION_TIME ?? '',
  }
}

export function buildSecurityPayload(values: SecurityFormValues): Record<string, string> {
  return buildScalarPayload(values, SECURITY_KEYS)
}

/* --------------------------------------------------------------------- */
/* Mobile Application tab                                                */
/* --------------------------------------------------------------------- */

const MOBILE_KEYS = [
  'ANDROID_VERSION',
  'IOS_VERSION',
  'APPLICATION_MAINTENANCE',
  'MAINTENANCE_CONTENT',
] as const

export function extractMobileAppValues(raw: ConfigApiResponse): MobileAppFormValues {
  const flat = flattenScalarEntries(raw)
  const maint: MaintenanceFlag =
    flat.APPLICATION_MAINTENANCE === '1' || flat.APPLICATION_MAINTENANCE === '0'
      ? flat.APPLICATION_MAINTENANCE
      : ''
  return {
    ANDROID_VERSION: flat.ANDROID_VERSION ?? '',
    IOS_VERSION: flat.IOS_VERSION ?? '',
    APPLICATION_MAINTENANCE: maint,
    MAINTENANCE_CONTENT: flat.MAINTENANCE_CONTENT ?? '',
  }
}

export function buildMobileAppPayload(
  values: MobileAppFormValues,
): Record<string, string> {
  return buildScalarPayload(values, MOBILE_KEYS)
}

/* --------------------------------------------------------------------- */
/* Yardi tab                                                              */
/* --------------------------------------------------------------------- */

const YARDI_KEYS = [
  'YARDI_PLATFORM',
  'YARDI_INTERFACE_ENTITY',
  'YARDI_INTERFACE_LICENSE',
  'YARDI_TARGET_ENV',
  'YARDI_DATA_FETCH_RANGE_BY_DAYS',
] as const

/**
 * Angular parity: YARDI_INTERFACE_ENTITY and YARDI_INTERFACE_LICENSE are
 * treated as write-only — they populate the `placeholder` attribute on the
 * input but don't prefill the input value. The admin types a new value to
 * overwrite. Non-sensitive Yardi fields prefill normally.
 */
export function extractYardiValues(raw: ConfigApiResponse): {
  values: YardiFormValues
  placeholders: YardiPlaceholders
} {
  const flat = flattenScalarEntries(raw)
  const placeholders: YardiPlaceholders = {
    interfaceEntity: flat.YARDI_INTERFACE_ENTITY ?? '',
    interfaceLicense: flat.YARDI_INTERFACE_LICENSE ?? '',
  }
  return {
    values: {
      YARDI_PLATFORM: flat.YARDI_PLATFORM ?? '',
      YARDI_INTERFACE_ENTITY: '',
      YARDI_INTERFACE_LICENSE: '',
      YARDI_TARGET_ENV: flat.YARDI_TARGET_ENV ?? '',
      YARDI_DATA_FETCH_RANGE_BY_DAYS: flat.YARDI_DATA_FETCH_RANGE_BY_DAYS ?? '',
    },
    placeholders,
  }
}

export function buildYardiPayload(values: YardiFormValues): Record<string, string> {
  return buildScalarPayload(values, YARDI_KEYS)
}

/**
 * Extract the Communication-tab subset from the raw /api/config response.
 * Mirrors Angular `loadData()` behaviour:
 *   - Each response entry is `{ name, value }`; flatten to { [name]: value }
 *   - EMAIL_WHITELIST === '*' means validation is OFF (email_validation='0')
 *     and the whitelist textbox clears to ''.
 *   - Any other value means validation is ON (email_validation='1').
 */
export function extractCommunicationValues(
  raw: ConfigApiResponse,
): CommunicationFormValues {
  const flat: Record<string, string> = {}
  Object.values(raw).forEach((entry) => {
    if (
      entry &&
      typeof entry === 'object' &&
      'name' in entry &&
      'value' in entry
    ) {
      const { name, value } = entry as { name: string; value: unknown }
      if (typeof name === 'string') {
        flat[name] = value == null ? '' : String(value)
      }
    }
  })

  const whitelist = flat.EMAIL_WHITELIST ?? ''
  const validationOff = whitelist === '*'

  const imapStatus = flat.IMAP_STATUS === 'ON' || flat.IMAP_STATUS === 'OFF'
    ? (flat.IMAP_STATUS as 'ON' | 'OFF')
    : ''

  const extInsurance =
    flat.SEND_TO_EXTERNAL_INSURANCE === 'YES' || flat.SEND_TO_EXTERNAL_INSURANCE === 'NO'
      ? (flat.SEND_TO_EXTERNAL_INSURANCE as 'YES' | 'NO')
      : ''

  return {
    email_validation: validationOff ? '0' : '1',
    EMAIL_WHITELIST: validationOff ? '' : whitelist,

    IMAP_HOST: flat.IMAP_HOST ?? '',
    IMAP_PORT: flat.IMAP_PORT ?? '',
    IMAP_USERNAME: flat.IMAP_USERNAME ?? '',
    IMAP_PASSWORD: flat.IMAP_PASSWORD ?? '',
    IMAP_STATUS: imapStatus,

    MAILGUN_DOMAIN: flat.MAILGUN_DOMAIN ?? '',

    DEFAULT_FIRST_REMINDER: flat.DEFAULT_FIRST_REMINDER ?? '',
    DEFAULT_SECOND_REMINDER: flat.DEFAULT_SECOND_REMINDER ?? '',
    ACTIVE_DEFAULT_FIRST_REMINDER: flat.ACTIVE_DEFAULT_FIRST_REMINDER ?? '',
    ACTIVE_DEFAULT_SECOND_REMINDER: flat.ACTIVE_DEFAULT_SECOND_REMINDER ?? '',

    SEND_TO_EXTERNAL_INSURANCE: extInsurance,
    EXTERNAL_INSURANCE_EMAIL: flat.EXTERNAL_INSURANCE_EMAIL ?? '',
  }
}

/**
 * Build the POST /api/config payload for Communication fields only.
 * Mirrors Angular `submitForm()` whitelist logic:
 *   - If email_validation === '0', EMAIL_WHITELIST is forced to '*'.
 *   - `email_validation` is never sent (server doesn't accept it).
 *   - Empty strings are dropped (Angular `getFilterValue` trims + drops falsy).
 */
export function buildCommunicationPayload(
  values: CommunicationFormValues,
): Record<string, string> {
  const payload: Record<string, string> = {}

  if (values.email_validation === '0') {
    payload.EMAIL_WHITELIST = '*'
  } else if (values.EMAIL_WHITELIST.trim() !== '') {
    payload.EMAIL_WHITELIST = values.EMAIL_WHITELIST.trim()
  }

  const scalarKeys = [
    'IMAP_HOST',
    'IMAP_PORT',
    'IMAP_USERNAME',
    'IMAP_PASSWORD',
    'IMAP_STATUS',
    'MAILGUN_DOMAIN',
    'DEFAULT_FIRST_REMINDER',
    'DEFAULT_SECOND_REMINDER',
    'ACTIVE_DEFAULT_FIRST_REMINDER',
    'ACTIVE_DEFAULT_SECOND_REMINDER',
    'SEND_TO_EXTERNAL_INSURANCE',
    'EXTERNAL_INSURANCE_EMAIL',
  ] as const

  scalarKeys.forEach((key) => {
    const raw = values[key]
    const trimmed = typeof raw === 'string' ? raw.trim() : ''
    if (trimmed !== '') {
      payload[key] = trimmed
    }
  })

  return payload
}

/**
 * Normalizes GET `/api/config/static/:filename` body so it matches Angular `HttpClient`:
 * empty content is often JSON-encoded as `""`; raw `responseType: 'text'` would leave two
 * quote characters visible in the textarea.
 */
export function coerceStaticFileBody(data: unknown): string {
  if (data == null) return ''
  if (typeof data === 'string') {
    const trimmed = data.trim()
    if (trimmed === '') return ''
    try {
      const parsed = JSON.parse(trimmed)
      if (typeof parsed === 'string') return parsed
    } catch {
      /* Not JSON — plain CSS/text */
    }
    return data
  }
  return ''
}

/**
 * GET `/api/config/static/:filename` — CSS text for Equifax styling, etc.
 * Mirrors Angular `SaveStyleCSSComponent.loadData()` (JSON-aware default parse + coercion).
 */
export async function getStaticConfigFile(filename: string): Promise<string> {
  try {
    const response = await apiClient.get<unknown>(
      `/api/config/static/${encodeURIComponent(filename)}`,
    )
    return coerceStaticFileBody(response.data)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load static config file')
  }
}

/**
 * POST `/api/config/static/:filename` with `{ content }`.
 * Mirrors Angular `SaveStyleCSSComponent.submitForm()` (+ analytics event).
 */
export async function saveStaticConfigFile(filename: string, content: string): Promise<void> {
  try {
    await apiClient.post(`/api/config/static/${encodeURIComponent(filename)}`, { content })
    trackApplicationEvent('style_saved', 'configuration')
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to save static config file')
  }
}
